'use client';

import Link from 'next/link';
import type { RestaurantRecord } from '@wl/api-client';
import { buildRestaurantUrl, RESOS_CDN_BASE } from '../config';
import { IMG } from '../../imgPrefix';

interface RestaurantCardProps {
  restaurant: RestaurantRecord;
  /** Booking date — YYYY-MM-DD. Forwarded to the detail-page URL when set. */
  date?: string;
  /** Party size. Forwarded to the detail-page URL when set. */
  people?: number;
  /** Time slot ("HH:mm" 24-h). Forwarded to the detail-page URL when set. */
  time?: string;
}

export function RestaurantCard({ restaurant, date, people, time }: RestaurantCardProps) {
  const detailUrl = (() => {
    const base = buildRestaurantUrl(
      restaurant.countrySlug,
      restaurant.stateSlug,
      restaurant.citySlug,
      restaurant.slug,
    );
    const p = new URLSearchParams();
    // `restaurantId` is the resOS id — carried on every listing → detail
    // link so `generateMetadata` on the detail page has a deterministic
    // fallback to fetch the record if the slug-only lookup fails. Keeps
    // SEO (title + description with city) working even when slugs
    // collide or the by-slug index is stale.
    if (restaurant.id) p.set('restaurantId', restaurant.id);
    if (date)   p.set('date', date);
    if (people) p.set('people', String(people));
    if (time)   p.set('time', time);
    const qs = p.toString();
    return qs ? `${base}?${qs}` : base;
  })();

  // Category chip currently disabled — keep the derivation logic
  // commented out so we can re-enable it later without re-plumbing
  // the addOns shape.
  // const bip = (restaurant.addOns?.bookItPlease ?? {}) as { cuisineTypes?: string[] };
  // const categoryText = bip.cuisineTypes?.[0] ?? 'Restaurant';

  // Neighborhood + Region + City + Country — address1 stands in for
  // "neighborhood" (resOS's finest-grained address line), state is
  // the region/province (e.g. "Greater London"), filtered to only
  // the parts this restaurant actually has.
  const locationText = [
    restaurant.address?.address1,
    restaurant.address?.state,
    restaurant.address?.city,
    restaurant.address?.country,
  ].filter(Boolean).join(', ');

  // resOS's own `image` field is only populated when the venue set a
  // primary photo through resOS itself. Venues onboarded solely through
  // the BookItPlease add-on carry their photo(s) under
  // `addOns.bookItPlease.images[]` instead (same fallback the checkout/
  // table-selection/confirmation pages already apply) — fall back to the
  // first one so the listing card isn't stuck on the placeholder.
  const bipImages = (restaurant.addOns as { bookItPlease?: { images?: { image: string }[] } } | undefined)
    ?.bookItPlease?.images;
  const primaryImagePath = restaurant.image || bipImages?.[0]?.image || undefined;

  const imageUrl = primaryImagePath
    ? primaryImagePath.startsWith('http')
      ? primaryImagePath
      : `${RESOS_CDN_BASE}${primaryImagePath}`
    : `${IMG}/image/no-image.png`;

  return (
    <div className="mb-3">
      <div className="Recommended-bx cutout-shape overflow-hidden">
        <div className="cutout-inner">
          <Link href={detailUrl} className="block">
            <img
              src={imageUrl}
              alt={`Dining room at ${restaurant.name}`}
              className="h-[180px] w-full object-contain bg-gray-100"
              onError={(e) => { (e.target as HTMLImageElement).src = `${IMG}/image/no-image.png`; }}
            />
          </Link>
          <div className="p-3">
            {/* <div className="card-display justify-content-between">
              <div className="pb-3">
                <div className="category-text">{categoryText}</div>
              </div>
            </div> */}

            <div className="heading">
              <h4>
                <Link href={detailUrl} className="text-black">
                  {restaurant.name}
                </Link>
              </h4>
            </div>

            {locationText && (
              <div className="pb-2 sports-text">
              <i className="text-lg fal fa-map-marker-alt pr-2"></i>
                {locationText}
              </div>
            )}

            <div className="pt-2 d-flex align-items-center justify-content-between Recommended-web-pad">
              <Link href={detailUrl}>
                <span className="btn btn-primary btn-sm getPropertyDetail list-book-now-btn">
                  Book Table
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
